import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import {
    IconChevronDown,
    IconCopy,
    IconDownload,
    IconFileTypeDocx,
    IconFileTypePdf,
    IconLayoutSidebarRightCollapse,
} from "@tabler/icons-react";

import { Text, Button, Menu } from "@fragments";

import "./styles.scss";

// Stage directions arrive between brackets ("[mostrar o movimento]"). They
// are cues to the person recording, not speech, so they read muted.
function renderSpeech(text) {
    return String(text)
        .split(/(\[[^\]]+\])/g)
        .filter(Boolean)
        .map((part, index) =>
            part.startsWith("[") && part.endsWith("]")
                ? <em key={index} className="script-block__cue">{part}</em>
                : <Fragment key={index}>{part}</Fragment>
        );
}

/**
 * The preview column: eyebrow + title + context in the header, the overview
 * and one row per block in the scrolling body, copy and download pinned in
 * the footer. Download opens a Popper (via Menu) with the two formats: PDF,
 * and .docx for Google Docs / Word.
 */
export default function ScriptPreview({ script, version, contextLine, onClose, onCopy, onDownloadPdf, onDownloadDocx }) {
    const { t } = useTranslation("contentModals");

    const downloadItems = [
        { id: "pdf", label: t("generateScript.preview.downloadPdf"), icon: <IconFileTypePdf size={16} stroke={1.75} />, onClick: onDownloadPdf },
        { id: "docx", label: t("generateScript.preview.downloadDocx"), icon: <IconFileTypeDocx size={16} stroke={1.75} />, onClick: onDownloadDocx },
    ];

    return (
        <section className="script-preview" aria-label={t("generateScript.preview.eyebrow")}>
            <header className="script-preview__header">
                <Button
                    variant="text"
                    color="inverse"
                    size="small"
                    icon={<IconLayoutSidebarRightCollapse />}
                    onClick={onClose}
                    tooltip={t("generateScript.preview.close")}
                    aria-label={t("generateScript.preview.close")}
                    preventLoading
                    className="script-preview__collapse"
                />
                <div className="script-preview__heading">
                    <Text.subheading contrast={6}>
                        {t("generateScript.preview.eyebrow")} · {t("generateScript.chat.version", { count: version })}
                    </Text.subheading>
                    <Text.h5 weight={500} contrast={9}>{script.title}</Text.h5>
                    {contextLine && <Text.caption>{contextLine}</Text.caption>}
                </div>
            </header>

            <div className="script-preview__body">
                <Text.p contrast={7} className="script-preview__overview">{script.overview}</Text.p>

                <ol className="script-preview__blocks">
                    {script.blocks.map((block, index) => (
                        <li key={index} className="script-block" style={{ "--i": index }}>
                            <div className="script-block__meta">
                                <Text.caption contrast={5}>{String(index + 1).padStart(2, "0")}</Text.caption>
                                <Text.caption>{t("generateScript.minutes", { count: block.minutes })}</Text.caption>
                            </div>
                            <div className="script-block__body">
                                <Text.label>{block.title}</Text.label>
                                <Text.p contrast={8} className="script-block__speech">{renderSpeech(block.script)}</Text.p>
                            </div>
                        </li>
                    ))}
                </ol>
            </div>

            <footer className="script-preview__footer">
                <Button size="small" color="overlay" startIcon={<IconCopy />} onClick={onCopy} preventLoading>
                    {t("generateScript.result.copy")}
                </Button>

                <Menu items={downloadItems} placement="top-end">
                    <Button size="small" color="inverse" startIcon={<IconDownload />} endIcon={<IconChevronDown />} preventLoading>
                        {t("generateScript.preview.download")}
                    </Button>
                </Menu>
            </footer>
        </section>
    );
}
